import { useState, useEffect, useRef } from "react";
import { diff } from "deep-object-diff";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { syncBoardTranslationsToService, updateTranslationsUsage } from "@util/Scripts";

/**
 * Syncs widget name/content translations to the translation service when widget settings are saved.
 *
 * @param {Array<{keyField: string, category: string, valuePrefix: string, condition?: Function}>} mapping
 *   - keyField:    field in widget settings holding the translation key (e.g. "btnTitle")
 *   - category:    translation category (e.g. "button.name")
 *   - valuePrefix: prefix for per-language value fields (e.g. "name" → "namepl", "nameen", ...)
 *   - condition:   optional (settings) => boolean — skip entry when false
 *
 * @returns {(settings: object) => void} call with current widget settings in handleSubmit
 *
 * @example
 * const syncTranslations = useWidgetTranslationSync([
 *   { keyField: "btnTitle", category: "button.name", valuePrefix: "name" },
 * ]);
 * // in handleSubmit:
 * syncTranslations(settings);
 */
export const useWidgetTranslationSync = (mapping) => {
   const intl = useIntl();
   const { allLanguages } = useSelector(({ settings }) => settings);

   return (settings) => {
      const translationsToSync = mapping
         .filter(
            ({ keyField, condition }) => settings[keyField] && (!condition || condition(settings)),
         )
         .map(({ keyField, category, valuePrefix }) => ({
            key: settings[keyField],
            category,
            values: allLanguages.reduce((acc, { language }) => {
               const val = settings[`${valuePrefix}${language}`];
               if (val != null) acc[language] = val;
               return acc;
            }, {}),
         }));

      if (translationsToSync.length === 0) return Promise.resolve();

      return syncBoardTranslationsToService(translationsToSync, allLanguages).then((results) => {
         if (results?.some((r) => r.status === "rejected")) {
            throw new Error("Translation sync partially failed");
         }
         updateTranslationsUsage(intl, true);
      });
   };
};

// Seeds current-locale service value into initial state so the modal doesn't show a stale board-stored copy.
export const useInitialTranslations = (fields) => {
   const { locale } = useSelector(({ settings }) => settings);
   const serviceTranslations = useSelector(({ translations }) => translations.translations);

   return fields.reduce((acc, { translationKey, prefix = "name" }) => {
      if (translationKey && serviceTranslations?.[translationKey]) {
         acc[`${prefix}${locale}`] = serviceTranslations[translationKey];
      }
      return acc;
   }, {});
};

export const useWidgetSettings = (initialValue, { sync = [], translations = [] } = {}) => {
   const syncTranslations = useWidgetTranslationSync(sync);
   const translationInit = useInitialTranslations(translations);
   const [settings, setSettings] = useState({ ...initialValue, ...translationInit });
   const handleChange = (value, name) => setSettings((prev) => ({ ...prev, [name]: value }));
   return { settings, setSettings, handleChange, syncTranslations };
};

export const useWindowSize = () => {
   // Initialize state with undefined width/height so server and client renders match
   // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
   const [windowSize, setWindowSize] = useState({
      width: undefined,
      height: undefined,
   });
   useEffect(() => {
      // Handler to call on window resize
      function handleResize() {
         // Set window width/height to state
         setWindowSize({
            width: window.innerWidth,
            height: window.innerHeight,
         });
      }
      // Add event listener
      window.addEventListener("resize", handleResize);
      // Call handler right away so state gets updated with initial window size
      handleResize();
      // Remove event listener on cleanup
      return () => window.removeEventListener("resize", handleResize);
   }, []); // Empty array ensures that effect is only run on mount
   return windowSize;
};

export const useImage = (filePath) => {
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(null);
   const [image, setImage] = useState(null);

   useEffect(() => {
      const fetchImage = async () => {
         try {
            const response = await import(`../assets${filePath}`); // change relative path to suit your needs
            setImage(response.default);
         } catch (err) {
            setError(err);
         } finally {
            setLoading(false);
         }
      };

      fetchImage();
   }, [filePath]);

   return {
      loading,
      error,
      image,
   };
};

// use if any of your dependency object/array.
// also known as useTheUltimateWorldDefyingExtraDeluxeEffect
export const useEffectPlus = (callback, dependencies) => {
   const prev = useRef(dependencies);

   useEffect(() => {
      const difference = Object.keys(diff(prev.current, dependencies));
      if (difference.length !== 0) {
         prev.current = dependencies;
         callback();
      }
   });
};

export const usePrevious = (value) => {
   const ref = useRef();
   useEffect(() => {
      ref.current = value;
   });
   return ref.current;
};
